<template>
  <div class="registration-form__step registration-form__step-password">
    <div class="registration-form__step__container">
      <div class="fields">
        <div class="fields-password">
          <text-input-fi
            id="password"
            ref="password"
            v-model.trim="password"
            name="password"
            type="password"
            autocomplete="new-password"
            data-test="password"
            :rules="passwordRules"
            :maxlength="120"
            :label="fields.passwordLabel?.value"
            required
            @blur="activatePasswordValidation($refs.password)"
          />

          <password-requirements
            :password="password"
            @valid-changed="passwordValidationChanged"
          />
        </div>
        <div class="fields-password-repeat">
          <text-input-fi
            id="password-repeat"
            ref="passwordRepeat"
            v-model.trim="passwordRepeat"
            name="password-repeat"
            type="password"
            autocomplete="new-password"
            data-test="password-repeat"
            :rules="
              enablePasswordRepeatValidation
                ? checkPasswordRepeat(fields)
                : dynamicRule(rules.email, enablePasswordRepeatValidation)
            "
            :maxlength="120"
            :label="fields.passwordRepeatLabel?.value"
            required
            @blur="enablePasswordRepeatValidation = true"
            @change="passwordRepeatChanged"
            @keyup="passwordRepeatChanged"
          />
        </div>
      </div>

      <div class="terms">
        <checkbox-custom-fi
          :value="termsAndConditions"
          :rules="rules.termsAndConditions"
          class="registration-form__checkbox"
          data-test="terms-and-conditions"
          :data-test-2="
            termsAndConditions ? 'terms-and-conditions-checked' : 'terms-and-conditions-unchecked'
          "
          required
          @checkbox-custom-changed="(value) => (termsAndConditions = value)"
        >
          <!-- eslint-disable vue/no-v-html -->
          <span
            v-html="
              labelContent('termsAndConditionsCheckboxLabel', [
                'termsAndConditionsLink',
                'dataPrivacylink',
                'additionalTermsAndConditionsLink',
              ])
            "
          />
          <!-- eslint-enable vue/no-v-html -->
        </checkbox-custom-fi>

        <checkbox-custom-fi
          v-if="fields.enableAdvertisingAgreement?.value"
          :value="advertisingConsent"
          class="registration-form__checkbox"
          data-test="advertising-agreement"
          :data-test-2="
            advertisingConsent ? 'advertising-agreement-checked' : 'advertising-agreement-unchecked'
          "
          @checkbox-custom-changed="(value) => (advertisingConsent = value)"
        >
          <!-- eslint-disable vue/no-v-html -->
          <span
            v-html="labelContent('advertisingAgreementCheckboxLabel', ['advertisingAgreement'])"
          />
          <!-- eslint-enable vue/no-v-html -->
        </checkbox-custom-fi>

        <div
          v-if="!noRecaptcha"
          ref="recaptcha"
          class="registration-form__recaptcha"
        >
          <vue-recaptcha
            ref="invisibleRecaptcha"
            load-recaptcha-script
            data-test="receptcha"
            :data-test-2="captchaValid ? 'receptcha-checked' : 'receptcha-unchecked'"
            :sitekey="sitekey"
            @verify="onVerify"
            @error="onError"
          />

          <div
            v-if="isCaptchaError && fields.googleCaptchaValidationMessage?.value"
            class="registration-form__recaptcha__error-message"
            :class="{ 'registration-form__recaptcha--error': isCaptchaError }"
          >
            {{ fields.googleCaptchaValidationMessage.value }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CheckboxCustomFi, PasswordRequirements, TextInputFi } from 'atoms';
import checkboxCustomLabelMixin from 'mixins/checkboxCustomLabelMixin';
import mdiIconsMixin from 'mixins/mdiIconsMixin';
import validatorsMixin from 'mixins/validatorsMixin';
import VueRecaptcha from 'vue3-recaptcha2';

import { checkPassword, checkPasswordRepeat } from '@/utils/validators';

export default {
  name: 'RegistrationStepPassword',
  components: {
    VueRecaptcha,
    PasswordRequirements,
    CheckboxCustomFi,
    TextInputFi,
  },
  mixins: [mdiIconsMixin, validatorsMixin, checkboxCustomLabelMixin],
  inject: ['fields'],
  props: {
    noRecaptcha: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['valid-changed'],
  data() {
    return {
      showPassword: false,
      showPasswordRepeat: false,
      enablePasswordValidation: false,
      enablePasswordRepeatValidation: false,
    };
  },
  computed: {
    sitekey() {
      return this.$fischer.recaptchaKey();
    },
    password: {
      get() {
        return this.$store.getters['registration/getPassword'];
      },
      set(password) {
        this.$store.dispatch('registration/setPassword', password);
        this.validate();
      },
    },
    passwordRepeat: {
      get() {
        return this.$store.getters['registration/getPasswordRepeat'];
      },
      set(passwordRepeat) {
        this.$store.dispatch('registration/setPasswordRepeat', passwordRepeat);
        this.validate();
      },
    },
    termsAndConditions: {
      get() {
        return this.$store.getters['registration/getTermsAndConditions'];
      },
      set(termsAndConditions) {
        this.$store.dispatch('registration/setTermsAndConditions', termsAndConditions);
        this.validate();
      },
    },
    advertisingConsent: {
      get() {
        return this.$store.getters['registration/getAdvertisingConsent'];
      },
      set(advertisingConsent) {
        this.$store.dispatch('registration/setAdvertisingConsent', advertisingConsent);
      },
    },
    isCaptchaError: {
      get() {
        return this.$store.getters['registration/getIsCaptchaError'];
      },
      set(isCaptchaError) {
        this.$store.dispatch('registration/setIsCaptchaError', isCaptchaError);
        this.validate();
      },
    },
    captchaResponse: {
      get() {
        return this.$store.getters['registration/getCaptchaResponse'];
      },
      set(captchaResponse) {
        this.$store.dispatch('registration/setCaptchaResponse', captchaResponse);
        this.validate();
      },
    },
    captchaValid() {
      if (!this.noRecaptcha && !IS_SHOWROOM) {
        return Boolean(this.captchaResponse && !this.isCaptchaError);
      }
      return true;
    },
  },
  async created() {
    if (this.password.length) {
      this.enablePasswordValidation = true;
    }
    if (this.passwordRepeat.length) {
      this.enablePasswordRepeatValidation = true;
    }
  },
  methods: {
    checkPassword,
    checkPasswordRepeat,
    onVerify(response) {
      if (response) {
        this.isCaptchaError = false;
        this.captchaResponse = response;
      }
    },
    onError(response) {
      if (response) {
        this.isCaptchaError = true;
      }
    },
    resetRecaptcha() {
      this.$refs.recaptcha.reset();
    },
    validate() {
      let isValid = false;
      if (this.$refs.password && this.$refs.passwordRepeat) {
        isValid = Boolean(
          this.password !== '' &&
            !this.$refs.password.errorBucket.length &&
            this.passwordRepeat !== '' &&
            !this.$refs.passwordRepeat.errorBucket.length &&
            this.termsAndConditions &&
            this.captchaValid,
        );
      }
      this.$emit('valid-changed', isValid);
    },
  },
};
</script>

<style lang="scss">
@import './registration-step-password-unscoped';
</style>

<style lang="scss" scoped>
@import './registration-step-password';
</style>
